import React, { forwardRef } from "react";
import DatePicker from "react-datepicker";
import { TextField, InputAdornment } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useTheme } from "@mui/material/styles";
import { useDesignTokens } from "../../../theme/useDesignTokens";
import { ACTION_BUTTON_HEIGHT } from "../../../constants/uiConstants";

const MuiDatePicker = ({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  disabled = false,
  size = "small",
  fullWidth = true,
  sx = {},
  InputLabelProps = {},
  ...props
}) => {
  const theme = useTheme();
  const { colors } = useDesignTokens();
  const primaryColor = colors?.primary?.main || theme.palette.primary.main;

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      height: ACTION_BUTTON_HEIGHT,
      boxSizing: "border-box",
      alignItems: "center",
      margin: 0,
    },
    "& .MuiInputBase-root": {
      borderBottom: "none !important",
    },
    "& .MuiInputBase-root::before": {
      display: "none !important",
      borderBottom: "none !important",
    },
    "& .MuiInputBase-root::after": {
      display: "none !important",
      borderBottom: "none !important",
    },
    "& .MuiInputBase-input": {
      height: `${ACTION_BUTTON_HEIGHT}px`,
      padding: "8.5px 40px 8.5px 14px",
      paddingLeft: "14px !important",
      paddingRight: "40px !important",
      lineHeight: "1.5",
      textAlign: "left",
      boxSizing: "border-box",
      borderBottom: "none !important",
      margin: "0 !important",
    },
    "& .MuiInputAdornment-root": {
      height: ACTION_BUTTON_HEIGHT,
      maxHeight: ACTION_BUTTON_HEIGHT,
      alignItems: "center",
      marginTop: 0,
      marginBottom: 0,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${primaryColor} !important`,
      borderWidth: "1px",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: `${primaryColor} !important`,
    },
    ...sx,
  };

  const CustomInput = forwardRef(({ value: inputValue, onClick }, ref) => (
    <TextField
      label={label}
      value={inputValue || ""}
      onClick={onClick}
      placeholder=""
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      variant="outlined"
      inputRef={ref}
      InputLabelProps={InputLabelProps}
      sx={{
        ...textFieldSx,
        "& .MuiInput-underline:before": {
          display: "none !important",
          borderBottom: "none !important",
        },
        "& .MuiInput-underline:after": {
          display: "none !important",
          borderBottom: "none !important",
        },
        "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
          display: "none !important",
          borderBottom: "none !important",
        },
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <CalendarTodayIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
  ));

  return (
    <div 
      style={{ 
        width: "100%",
        position: "relative",
        display: "block",
        margin: 0,
        padding: 0,
      }}
      className="mui-datepicker-wrapper"
    >
      <style>{`
        .mui-datepicker-wrapper {
          margin: 0 !important;
          padding: 0 !important;
        }
        .mui-datepicker-wrapper .react-datepicker-wrapper,
        .mui-datepicker-wrapper .react-datepicker__input-container {
          display: block !important;
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
          border-bottom: none !important;
          box-shadow: none !important;
        }
        .mui-datepicker-wrapper .react-datepicker-wrapper input {
          border-bottom: none !important;
          box-shadow: none !important;
        }
        .mui-datepicker-wrapper .react-datepicker-wrapper::after,
        .mui-datepicker-wrapper .react-datepicker__input-container::after,
        .mui-datepicker-wrapper .react-datepicker-wrapper::before,
        .mui-datepicker-wrapper .react-datepicker__input-container::before {
          display: none !important;
          border-bottom: none !important;
          content: none !important;
        }
      `}</style>
      <DatePicker
        selected={value}
        onChange={onChange}
        minDate={minDate}
        maxDate={maxDate}
        customInput={<CustomInput />}
        dateFormat="MM/dd/yyyy"
        popperPlacement="bottom-start"
        portalId="root-portal"
        {...props}
      />
    </div>
  );
};

export default MuiDatePicker;
